import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
  ReferenceLine,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import type { productionAsset } from 'types/production.types';
import { ThemePalette } from 'mui.theme';
import { getAssetChartDataSource } from '../../utils';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { getDataUnit } from 'views/Production/Hauler/component/utils';
import {
  convertMinutesToHours,
  convertsecondsToHours,
} from 'views/Utilization/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useMemo } from 'react';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  data: productionAsset;
  selectedSource: string;
  height?: string;
  aggregation: string;
}

const AssetItemChart = ({
  data,
  selectedSource,
  height = '230px',
  aggregation,
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { unitSystem } = useCustomerInfo();
  const { date_stats: dateLoad } = data;

  const formattedDateLoad = Object.keys(dateLoad).map((date) => ({
    date,
    ...dateLoad[date],
    idling_duration: convertsecondsToHours(dateLoad[date].idling_duration),
    productive_duration: convertMinutesToHours(
      dateLoad[date].utilization_engine_working_minute
    ),
    productionValue: dateLoad[date].max_load_ton,
    utilization_by_shift_hours: dateLoad[date].utilization_by_shift_hours,
  }));

  let dataUnit = getDataUnit(selectedSource, unitSystem);
  dataUnit = dataUnit ? `(${dataUnit})` : '';

  const chartData = formattedDateLoad.map((date: any) => {
    let value = date[getAssetChartDataSource(selectedSource)]?.toString();
    if (selectedSource === 'Utilization') {
      value = Number(value) * 100;
    }
    if (value === null || isNaN(value)) {
      value = 0;
    }
    return {
      date: date.date,
      value: convertToTwoDecimalPlaces(value),
    };
  });
  chartData.sort((a, b) => {
    const dateA = dayjs(a.date).unix();
    const dateB = dayjs(b.date).unix();
    return dateA - dateB;
  });

  const format = useMemo(() => {
    return aggregation === 'Monthly' ? 'DD MMM' : DATE_FORMAT;
  }, [aggregation]);

  const formatTimestamp = (timestamp: string): string => {
    const formattedDate = convertStdDateString(timestamp, format);
    return formattedDate;
  };
  // Calculate the average
  const validEntries = chartData.filter(
    (entry: any) =>
      entry.value !== null && !isNaN(entry.value) && entry.value !== 0
  );

  // Normalize selected source (lowercase + remove spaces)
  const sourceKey = selectedSource.toLowerCase().replace(/ /g, '');

  let average = 0;

  if (sourceKey === 'productionperhour') {
    average = Number(data?.avg_load_per_hour) || 0;
  } else if (sourceKey === 'utilization') {
    average = Number((data?.avg_utilization_by_shift_hours || 0) * 100);
  } else {
    average =
      validEntries.length > 0
        ? validEntries.reduce(
            (sum: number, entry: any) => sum + parseFloat(entry.value),
            0
          ) / validEntries.length
        : 0;
  }

  // Find max value
  const maxValue = validEntries.length
    ? Math.max(...validEntries.map((entry) => parseFloat(entry.value)))
    : 0;

  return (
    <Box sx={{ paddingTop: '10px' }}>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height,
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={700}
            height={300}
            data={chartData}
            margin={{
              top: 20,
              right: 100,
              left: 5,
              bottom: 5,
            }}
            barSize={30}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTimestamp}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={[0, Math.ceil(maxValue * 1.1)]}
              label={{
                value: `${selectedSource} ${dataUnit}`,
                angle: 270,
                position: 'insideLeft',
                fontSize: 10,
                textAnchor: 'middle',
                dy: 50,
              }}
            />
            <Tooltip
              cursor={{
                fill: enableDarkTheme
                  ? `${palette.primary}26`
                  : `${palette.primary}1a`,
              }}
              wrapperStyle={{ outline: 'none', fontSize: '11px' }}
              contentStyle={{
                backgroundColor: enableDarkTheme
                  ? 'rgba(0,0,0, 0.6)'
                  : 'rgba(255, 255, 255, 0.6)',
                borderColor: 'transparent',
                borderRadius: '4px',
              }}
              itemStyle={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
              }}
              labelFormatter={(value: string) =>
                convertStdDateString(value, format)
              }
            />
            <Bar
              dataKey="value"
              fill={palette.primary}
              activeBar={
                <Rectangle
                  fill={`${palette.primary}66`}
                  stroke={palette.primary}
                />
              }
            />
            <ReferenceLine
              y={average}
              label={{
                value:
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                    'productionperhour' ||
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                    'utilization'
                    ? `${truncateTo2dp(average?.toFixed(2))} ${dataUnit}`
                    : `Avg ${average?.toFixed(2)}  ${dataUnit}/Day`,
                position: 'right',
                fontSize: '10px',
              }}
              stroke={palette.primary}
              strokeDasharray="3 3"
              isFront
            />
          </BarChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

export default AssetItemChart;
